import { ReactElement } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { IPowerOnOffReport } from "../../types";
import { IContentAreaProps } from "../../../../../types";
import StyledTableRow from "../../../../../components/styledTableRow";
import TableContainer  from "../../../../../components/tableContainer";

interface IAvgPowerOnOffChartProps extends IContentAreaProps {
  data: IPowerOnOffReport[];
}

interface IColumn {
  id: keyof IPowerOnOffReport  | "missedHeat";
  label: string;
  minWidth: number;
  align?: "right";
  format?: (value: number) => string;
}

const columns: IColumn[] = [
  { id: "day", label: "Day", minWidth: 80, align: "right" },
  { id: "total", label: "Count of Tons", minWidth: 80, align: "right" },
  {
    id: "billetTons",
    label: "Billet Tons",
    minWidth: 80,
    align: "right",
    format: value => value.toFixed(0)
  },
  {
    id: "avg_powerON",
    label: "Power ON",
    minWidth: 80,
    align: "right",
    format: value => value.toFixed(0)
  },
  {
    id: "avg_powerOFF",
    label: "Power Off",
    minWidth: 80,
    align: "right",
    format: value => value.toFixed(0)
  },
  {
    id: "avg_tap_to_tap",
    label: "Tap to Tap",
    minWidth: 80,
    align: "right",
    format: value => value.toFixed(0)
  },
  {
    id: "avg_weight",
    label: "Tons x Heat",
    minWidth: 80,
    align: "right",
    format: value => value.toFixed(0)
  },
  {
    id: "missedHeat",
    label: "Missed Heat",
    minWidth: 80,
    align: "right",
    format: value => value.toFixed(0)
  },
];

const HeatTable = ({ data, parentSize }: IAvgPowerOnOffChartProps): ReactElement => {
  const consolidate: IPowerOnOffReport = {
    day: 0,
    total: 0,
    factoryDay: 0,
    avg_powerON: 0,
    avg_powerOFF: 0,
    avg_tap_to_tap: 0,
    avg_weight: 0,
    billetTons: 0
  };

  data.forEach(row => {
    consolidate.total += row.total;
    consolidate.avg_powerON += row.avg_powerON;
    consolidate.avg_powerOFF += row.avg_powerOFF;
    consolidate.avg_tap_to_tap += row.avg_tap_to_tap;
    consolidate.avg_weight += row.avg_weight;
    consolidate.billetTons += row.billetTons;
  });

  consolidate.avg_powerON = consolidate.avg_powerON / data.length;
  consolidate.avg_powerOFF = consolidate.avg_powerOFF / data.length;
  consolidate.avg_tap_to_tap = consolidate.avg_tap_to_tap / data.length;
  consolidate.avg_weight = consolidate.avg_weight / data.length;

  const headerCellStyle = { backgroundColor: "#b0bec5" } as const;
  const footerCellBaseStyle = { backgroundColor: "#b0bec5", fontWeight: "bold" } as const;

  return (
    <TableContainer style={{ height: parentSize.height - 70 }}>
      <Table stickyHeader={true} size={"small"}>
        <TableHead>
          <TableRow>
            {columns.map((column) =>
              <TableCell
                key={column.id}
                align={column.align}
                style={{ ...headerCellStyle, minWidth: column.minWidth }}>
                {column.label}
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((row, index) => {
            return (
              <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`day-row-${index}`}>
                {columns.map((column: IColumn) => {
                  if (column.id === "missedHeat") {
                    return (
                      <TableCell key={column.id} align={column.align}>
                        ---
                      </TableCell>
                    );
                  }

                  const value = row[column.id];

                  return (
                    <TableCell key={column.id} align={column.align}>
                      {column.format ? column.format(value) : value}
                    </TableCell>
                  );
                })}
              </StyledTableRow>
            );
          })
          }
          <TableRow>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              Total
            </TableCell>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              { consolidate.total }
            </TableCell>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              { consolidate.billetTons.toFixed(0) }
            </TableCell>
            <TableCell
              style={{ ...footerCellBaseStyle, width: 80 }}
              align="right">
              { consolidate.avg_powerON.toFixed(0) }
            </TableCell>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              { consolidate.avg_powerOFF.toFixed(0) }
            </TableCell>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              { consolidate.avg_tap_to_tap.toFixed(0) }
            </TableCell>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              { consolidate.avg_weight.toFixed(0) }
            </TableCell>
            <TableCell
              align="right"
              style={{ ...footerCellBaseStyle, width: 80 }}>
              ---
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HeatTable;
